/**
 * Scope (`iim_fields.applies_to`) for a field created from the Capture side.
 *
 * BACKGROUND — the two read paths disagree, and the default picked the wrong side.
 *
 *   Capture reads  applies_to = 'both' OR the requested scope
 *                  (capture-app/src/routes/templates.ts:252)
 *   Library reads  applies_to IN ('both','library')
 *                  (supabase/migrations/20260703_field_assoc_library_visible.sql:54)
 *
 * Index field DEFINITIONS are shared — one store, both products read and write it
 * (decisions/shared/2026-08-12-definitions-shared-published-documents-belong-to-library.md:
 * "A user who defines a document type or an index field in Capture must not have to
 * define it again in Library."). The Capture create form nevertheless defaulted its
 * scope selector to 'capture', so a field created at the default was invisible in
 * Library — against that principle. The default is now 'both'.
 *
 * The selector STAYS. Creating a genuinely Capture-only field is a real need — a
 * field that exists to drive a data export and has nothing to do with the Library.
 * The default is what changed, not the capability.
 */

/** The scopes the Capture form offers. Library-only is not creatable from here. */
export const FIELD_SCOPES = ['both', 'capture'];

/**
 * Shared, because definitions are shared. A field created in Capture is available
 * in Library without recreating it.
 */
export const DEFAULT_FIELD_SCOPE = 'both';

/**
 * Whether to render the scope selector at all.
 *
 * Hidden only when we KNOW the tenant has no Library (`hasLibrary === false`).
 *
 * WHY: the selector asks "does this field belong in the Library?" — a meaningless
 * question for a customer who has no Library. Hiding it is not cosmetic; it removes
 * a choice the user has no basis to make.
 *
 * `null`/`undefined` means the flag is unknown, and unknown is NOT "no Library".
 * Hiding on unknown would silently remove the Capture-only capability from tenants
 * that do have a Library, so unknown shows the selector.
 */
export function shouldShowScopeSelector(hasLibrary) {
  return hasLibrary !== false;
}

/**
 * The applies_to value to send to POST /api/capture/templates/field-library.
 *
 * For a tenant WITHOUT Library this returns 'both' regardless of what is in form
 * state — the selector is hidden, so there is no user choice to honour.
 *
 * WHY 'both' and not 'capture' for a Library-less tenant: if they add Library later,
 * their existing fields are already there. Writing 'capture' would hand them an empty
 * field list on upgrade, which looks broken. 'both' costs nothing while they have no
 * Library — nothing reads the Library side of the flag — and is correct the moment
 * they do.
 *
 * An unrecognised scope falls back to the shared default rather than being passed
 * through; the server also validates (templates.ts:272), and this keeps the two ends
 * agreeing instead of relying on the server to silently repair form state.
 */
export function resolveFieldScope({ hasLibrary, scope } = {}) {
  if (hasLibrary === false) return 'both';
  return FIELD_SCOPES.includes(scope) ? scope : DEFAULT_FIELD_SCOPE;
}
